import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Toastify, { notifyError } from "../../utils/tostfy";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import downArrow from "../../assets/image/down-arrow.svg";
import DownloadIcon from "../../assets/image/download.svg";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import {
  appendDateRangeParams,
  DATE_PARAM_KEYS,
  formatRangeLabel,
  validateDateRange as validateDateRangeUtil,
} from "../../utils/dateRange";
import { formatDateTime } from "../../utils/formatDateTime";
import { useAdminCsvReady } from "../../hooks/useAdminCsvReady";
import {
  isReportCsvType,
  patchReportLogRow,
  resolveCsvStatusLabel,
} from "../../utils/adminCsvSocket";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
import MonthYearPicker from "../../Components/MonthYearPicker/MonthYearPicker";
import { getDefaultSalaryAwardsMonth } from "../../Components/MonthYearPicker/monthYearBounds";
import {
  SEARCH_MAX_LENGTH,
  clampSearchInput,
  normalizePastedSearchInput,
} from "../../utils/search";

const REPORT_TABS = [
  {
    key: "trip-expense",
    label: "Trip Expense",
    type: "admin_expense_report",
    exportEndpoint: endpoint.createExpenseReportCsv,
    locationParam: "mipl_location_id",
  },
  {
    key: "daily-progress",
    label: "Daily Progress",
    type: "admin_dpr_report",
    exportEndpoint: endpoint.createDPRCsv,
    locationParam: "mipl_location",
  },
  {
    key: "driver-expenses",
    label: "Driver List",
    type: "admin_driver_report",
    exportEndpoint: endpoint.createDriverReportCsv,
    locationParam: "mipl_location_id",
  },
  {
    key: "trip",
    label: "Trip Detail",
    type: "admin_trip_report",
    exportEndpoint: endpoint.createTripCsv,
    locationParam: "mipl_location_id",
    matchLabels: ["Trip", "Trip Detail"],
  },
  {
    key: "driver-salary",
    label: "Driver Salary",
    type: "admin_driver_salary_report",
    exportEndpoint: endpoint.createDriverSalaryReport,
    locationParam: "mipl_location_id",
    matchLabels: ["Driver Salary"],
  },
];

const LOG_COLUMNS = [
  "S.No.",
  "File Name",
  "Date Range",
  "MIPL Location",
  "Generated On",
  "Status",
  "Action",
];

const TRIP_LOG_COLUMNS = [
  "S.No.",
  "File Name",
  "Date Range",
  "MIPL Location",
  "Company Name",
  "Company Location",
  "Generated On",
  "Status",
  "Action",
];

const DRIVER_SALARY_LOG_COLUMNS = [
  "S.No.",
  "File Name",
  "Month",
  "MIPL Location",
  "Driver",
  "Generated On",
  "Status",
  "Action",
];

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const mapCompany = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.company_name || "Unknown Company",
});

const extractList = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatFilterValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return value.name || value.location_name || value.company_name || value._id || value.id || "";
  }
  return String(value).trim();
};

function resolveLogLocation(item) {
  const filter = item?.filter ?? item?.filters;
  return (
    item?.mipl_location_name ||
    item?.location_name ||
    item?.mipl_location ||
    (typeof filter === "object" && filter != null
      ? formatFilterValue(
          filter.mipl_location_name ||
            filter.location_name ||
            filter.mipl_location ||
            filter.mipl_location_id
        )
      : "") ||
    "—"
  );
}

function resolveLogDateRange(filter) {
  if (filter == null || filter === "") return "—";
  if (typeof filter === "string") return "—";
  if (typeof filter !== "object") return "—";

  if (filter.from_date && filter.to_date) {
    return `${filter.from_date} to ${filter.to_date}`;
  }
  if (filter.from_date) return `From ${filter.from_date}`;
  if (filter.to_date) return `To ${filter.to_date}`;
  return "—";
}

function resolveLogMonth(filter) {
  if (!filter || typeof filter !== "object") return "—";
  const month = filter.month;
  const year = filter.year;
  if (!month || !year) return "—";
  const m = String(month).padStart(2, "0");
  const d = new Date(Number(year), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return `${m}/${year}`;
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function resolveLogDriver(filter, item) {
  const fromItem =
    item?.driver_name ||
    item?.full_name ||
    item?.driver_ref_id ||
    "";
  if (fromItem) return formatFilterValue(fromItem) || "—";
  if (!filter || typeof filter !== "object") return "—";
  return (
    formatFilterValue(
      filter.driver_name ||
        filter.full_name ||
        filter.driver_ref_id ||
        filter.search ||
        filter.driver_id
    ) || "—"
  );
}

function resolveLogCompany(item) {
  const filter = item?.filter ?? item?.filters;
  return (
    formatFilterValue(item?.company_name || item?.company || item?.company_id) ||
    (typeof filter === "object" && filter != null
      ? formatFilterValue(
          filter.company_name || filter.companyName || filter.company || filter.company_id
        )
      : "") ||
    "—"
  );
}

function resolveLogCompanyLocation(item) {
  const filter = item?.filter ?? item?.filters;
  return (
    formatFilterValue(
      item?.company_location_name || item?.companyLocationName || item?.company_location
    ) ||
    (typeof filter === "object" && filter != null
      ? formatFilterValue(
          filter.company_location_name ||
            filter.companyLocationName ||
            filter.company_location ||
            filter.company_location_id
        )
      : "") ||
    "—"
  );
}

function formatReportTypeLabel(type) {
  if (!type) return "—";
  return String(type)
    .replace(/^admin_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveExportStatus(item) {
  const link = item?.csv_link || item?.file_url || item?.download_url || "";
  return resolveCsvStatusLabel(item?.status, link);
}

const StatusBadge = ({ status }) => {
  const styles = {
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    Complete: "bg-green-50 text-green-700 border-green-200",
    Failed: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-3 py-0.5 text-xs font-UbuntuMedium ${
        styles[status] || styles.Pending
      }`}
    >
      {status}
    </span>
  );
};

const extractReportCsvLogList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.csv_log_data)) return data.csv_log_data;
  if (Array.isArray(data?.report_csv_log)) return data.report_csv_log;
  return [];
};

const mapReportCsvLogRow = (item, index) => {
  const link = item?.csv_link || item?.file_url || item?.download_url || "";
  const filter = item?.filter ?? item?.filters;
  const fileName =
    item?.file_name ||
    item?.filename ||
    (link ? decodeURIComponent(String(link).split("/").pop()?.split("?")[0] || "") : "") ||
    "—";

  return {
    id: item?._id || item?.id || item?.csv_log_id || `report-log-${index}`,
    reportType: item?.sheet_title || formatReportTypeLabel(item?.type) || "—",
    fileName,
    dateRange: resolveLogDateRange(filter),
    month: resolveLogMonth(filter),
    driver: resolveLogDriver(filter, item),
    location: resolveLogLocation(item),
    companyName: resolveLogCompany(item),
    companyLocation: resolveLogCompanyLocation(item),
    createdAt: formatDateTime(item?.createdAt || item?.created_at),
    status: resolveExportStatus(item),
    downloadUrl: link,
    rawType: item?.type || "",
  };
};

const LocationSelect = ({
  value,
  onChange,
  locations,
  loading,
  disabled,
  ariaLabel = "MIPL Location",
  placeholder = "All MIPL locations",
  loadingPlaceholder = "Loading locations…",
  disabledPlaceholder = "",
}) => (
  <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
    <select
      className="h-[44px] w-full appearance-none rounded-lg border border-[#0B3E64]/40 bg-white px-3 pr-9 text-sm outline-none focus:border-[#0B3E64] disabled:cursor-not-allowed disabled:opacity-70"
      value={value}
      onChange={onChange}
      disabled={disabled || loading}
      aria-label={ariaLabel}
    >
      <option value="">
        {disabled && disabledPlaceholder
          ? disabledPlaceholder
          : loading
          ? loadingPlaceholder
          : placeholder}
      </option>
      {locations.map((loc) => (
        <option key={loc.id} value={loc.id}>
          {loc.name}
        </option>
      ))}
    </select>
    <img
      src={downArrow}
      alt=""
      aria-hidden
      className="pointer-events-none absolute right-3 top-1/2 h-[7px] w-[7px] -translate-y-1/2"
    />
  </div>
);

const defaultSalaryMonth = getDefaultSalaryAwardsMonth();

const Reports = () => {
  const { setSubheading } = useOutletContext();

  const [activeTab, setActiveTab] = useState(REPORT_TABS[0].key);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  const [miplLocationId, setMiplLocationId] = useState("");
  const [miplLocations, setMiplLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companyLocationId, setCompanyLocationId] = useState("");
  const [companyLocations, setCompanyLocations] = useState([]);
  const [companyLocationsLoading, setCompanyLocationsLoading] = useState(false);

  const [salarySearch, setSalarySearch] = useState("");
  const [salaryDriverId, setSalaryDriverId] = useState("");
  const [salaryMonth, setSalaryMonth] = useState(defaultSalaryMonth.month);
  const [salaryYear, setSalaryYear] = useState(defaultSalaryMonth.year);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);

  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const activeTabConfig = useMemo(
    () => REPORT_TABS.find((t) => t.key === activeTab) || REPORT_TABS[0],
    [activeTab]
  );
  const isTripReport = activeTabConfig.key === "trip";
  const isDriverSalaryReport = activeTabConfig.key === "driver-salary";

  const logColumns = isTripReport
    ? TRIP_LOG_COLUMNS
    : isDriverSalaryReport
      ? DRIVER_SALARY_LOG_COLUMNS
      : LOG_COLUMNS;
  const colSpan = logColumns.length;
  const rangeText = formatRangeLabel(appliedDateFrom, appliedDateTo);

  const selectedLocationName = useMemo(() => {
    if (!miplLocationId) return "All MIPL locations";
    return miplLocations.find((l) => l.id === miplLocationId)?.name || "—";
  }, [miplLocationId, miplLocations]);

  const selectedCompanyName = useMemo(() => {
    if (!companyId) return "All companies";
    return companyList.find((c) => c.id === companyId)?.name || "—";
  }, [companyId, companyList]);

  const selectedCompanyLocationName = useMemo(() => {
    if (!companyLocationId) return "All company locations";
    return companyLocations.find((l) => l.id === companyLocationId)?.name || "—";
  }, [companyLocationId, companyLocations]);

  const selectedDriverName = useMemo(() => {
    if (!salaryDriverId) return "All drivers";
    const match = driverOptions.find((d) => d.id === salaryDriverId);
    return match?.name || "—";
  }, [salaryDriverId, driverOptions]);

  const selectedSalaryMonthLabel = useMemo(() => {
    if (!salaryMonth || !salaryYear) return "—";
    const d = new Date(Number(salaryYear), Number(salaryMonth) - 1, 1);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }, [salaryMonth, salaryYear]);

  const hasAnyFilter = isDriverSalaryReport
    ? Boolean(
        salarySearch.trim() ||
          salaryDriverId ||
          miplLocationId ||
          salaryMonth !== defaultSalaryMonth.month ||
          salaryYear !== defaultSalaryMonth.year
      )
    : Boolean(
        dateFrom ||
          dateTo ||
          appliedDateFrom ||
          appliedDateTo ||
          miplLocationId ||
          (isTripReport && (companyId || companyLocationId))
      );

  useEffect(() => {
    setSubheading("Reports");
    return () => setSubheading("");
  }, [setSubheading]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const raw = Array.isArray(d) ? d : Array.isArray(d?.location_data) ? d.location_data : [];
          setMiplLocations(raw.map(mapLocation).filter((x) => x.id && x.name));
        } else {
          notifyError(getApiErrorMessage(response, "Failed to load MIPL locations"));
          setMiplLocations([]);
        }
      } catch {
        notifyError("Failed to load MIPL locations");
        setMiplLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.listOfAllCompany, {});
        if (response?.data?.code === 200) {
          const raw = extractList(response?.data?.data, ["company_data"]);
          setCompanyList(raw.map(mapCompany).filter((x) => x.id && x.name));
        } else {
          notifyError(getApiErrorMessage(response, "Failed to load companies"));
          setCompanyList([]);
        }
      } catch {
        notifyError("Failed to load companies");
        setCompanyList([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const fetchCompanyLocations = useCallback(async (selectedCompanyId) => {
    if (!selectedCompanyId) {
      setCompanyLocations([]);
      setCompanyLocationId("");
      return;
    }

    setCompanyLocationsLoading(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoint.listOfCompanyLocation}?company_id=${encodeURIComponent(selectedCompanyId)}`,
        {}
      );
      if (response?.data?.code === 200) {
        const raw = extractList(response?.data?.data, ["location_data"]);
        setCompanyLocations(raw.map(mapLocation).filter((x) => x.id && x.name));
      } else {
        notifyError(getApiErrorMessage(response, "Failed to load company locations"));
        setCompanyLocations([]);
      }
    } catch {
      notifyError("Failed to load company locations");
      setCompanyLocations([]);
    } finally {
      setCompanyLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanyLocations(companyId);
  }, [companyId, fetchCompanyLocations]);

  const fetchDriverOptions = useCallback(async () => {
    if (!isDriverSalaryReport) return;

    setDriversLoading(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "100",
        search: clampSearchInput(salarySearch).trim(),
      });
      if (miplLocationId) {
        params.set("mipl_location_id", miplLocationId);
      }

      const response = await handleApiCall(
        "get",
        `${endpoint.listOfDriver}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const raw = Array.isArray(d?.driver_data)
          ? d.driver_data
          : Array.isArray(d?.drivers)
            ? d.drivers
            : Array.isArray(d)
              ? d
              : [];
        const options = raw
          .map((item) => ({
            id: item?._id || item?.id || "",
            name:
              item?.full_name ||
              item?.driver_name ||
              item?.driver_ref_id ||
              "Unknown Driver",
            refId: item?.driver_ref_id ? String(item.driver_ref_id) : "",
          }))
          .filter((x) => x.id);
        setDriverOptions(options);
        setSalaryDriverId((prev) =>
          prev && options.some((x) => x.id === prev) ? prev : ""
        );
      } else {
        setDriverOptions([]);
        notifyError(getApiErrorMessage(response, "Failed to load drivers"));
      }
    } catch {
      setDriverOptions([]);
      notifyError("Failed to load drivers");
    } finally {
      setDriversLoading(false);
    }
  }, [isDriverSalaryReport, salarySearch, miplLocationId]);

  useEffect(() => {
    if (!isDriverSalaryReport) return undefined;

    const timer = setTimeout(() => {
      fetchDriverOptions();
    }, 300);

    return () => clearTimeout(timer);
  }, [isDriverSalaryReport, fetchDriverOptions]);

  const fetchReportCsvLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTabConfig.type) params.set("type", activeTabConfig.type);

      const url = params.toString()
        ? `${endpoint.getReportCsvLog}?${params.toString()}`
        : endpoint.getReportCsvLog;

      const response = await handleApiCall("get", url, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        let list = extractReportCsvLogList(d).map(mapReportCsvLogRow);
        if (activeTabConfig.type) {
          const matchLabels = activeTabConfig.matchLabels || [activeTabConfig.label];
          list = list.filter(
            (row) =>
              !row.rawType ||
              row.rawType === activeTabConfig.type ||
              matchLabels.some((label) =>
                row.reportType.toLowerCase().includes(label.toLowerCase())
              )
          );
        }
        setLogRows(list);
      } else {
        setLogRows([]);
        notifyError(getApiErrorMessage(response, "Failed to load report log"));
      }
    } catch {
      setLogRows([]);
      notifyError("Failed to load report log");
    } finally {
      setLogLoading(false);
    }
  }, [activeTabConfig]);

  useEffect(() => {
    fetchReportCsvLog();
  }, [fetchReportCsvLog]);

  const handleCsvReady = useCallback(
    (payload) => {
      if (!isReportCsvType(payload?.type)) return;

      const logId = payload?.csv_log_id;
      setLogRows((prev) => {
        const idx = logId ? prev.findIndex((row) => row.id === logId) : -1;
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = patchReportLogRow(prev[idx], payload);
          return next;
        }
        return prev;
      });

      if (!logId || payload.type === activeTabConfig.type) {
        fetchReportCsvLog();
      }
    },
    [activeTabConfig.type, fetchReportCsvLog]
  );

  useAdminCsvReady(handleCsvReady);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "driver-salary") {
      const defaults = getDefaultSalaryAwardsMonth();
      setSalaryMonth(defaults.month);
      setSalaryYear(defaults.year);
    }
  };

  const handleApplyDates = () => {
    const dateCheck = validateDateRangeUtil(dateFrom, dateTo, { requireBoth: true });
    if (!dateCheck.valid) {
      return false;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    return true;
  };

  const handleClearDates = () => {
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
  };

  const handleClearFilters = () => {
    handleClearDates();
    setMiplLocationId("");
    setCompanyId("");
    setCompanyLocationId("");
    setCompanyLocations([]);
    setSalarySearch("");
    setSalaryDriverId("");
    const defaults = getDefaultSalaryAwardsMonth();
    setSalaryMonth(defaults.month);
    setSalaryYear(defaults.year);
  };

  const buildExportParams = () => {
    const params = new URLSearchParams();

    if (isDriverSalaryReport) {
      const search = clampSearchInput(salarySearch).trim();
      if (search) params.set("search", search);
      if (salaryDriverId) params.set("driver_id", salaryDriverId);
      if (salaryMonth) params.set("month", String(salaryMonth).padStart(2, "0"));
      if (salaryYear) params.set("year", String(salaryYear));
      if (miplLocationId) {
        params.set(activeTabConfig.locationParam, miplLocationId);
      }
      return params;
    }

    const hasFullDateRange = Boolean(appliedDateFrom && appliedDateTo);
    if (hasFullDateRange) {
      appendDateRangeParams(
        params,
        { start: appliedDateFrom, end: appliedDateTo },
        DATE_PARAM_KEYS.fromTo
      );
    }
    if (miplLocationId) {
      params.set(activeTabConfig.locationParam, miplLocationId);
    }
    if (isTripReport) {
      if (companyId) {
        params.set("company_id", companyId);
      }
      if (companyLocationId) {
        params.set("company_location_id", companyLocationId);
      }
    }
    return params;
  };

  const handleExport = async () => {
    if (!isDriverSalaryReport) {
      const dateCheck = validateDateRangeUtil(appliedDateFrom, appliedDateTo);
      if (!dateCheck.valid) {
        notifyError(dateCheck.message);
        return;
      }
    }

    setExportLoading(true);
    try {
      const params = buildExportParams();
      const qs = params.toString();
      const exportUrl = qs
        ? `${activeTabConfig.exportEndpoint}?${qs}`
        : activeTabConfig.exportEndpoint;
      const response = await handleApiCall("get", exportUrl, {});

      if (response?.data?.code === 200) {
        await fetchReportCsvLog();
        return;
      }

      notifyError(getApiErrorMessage(response, "Failed to export report"));
    } catch {
      notifyError("Failed to export report");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownload = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
    <div className="font-UbuntuRegular">
      <div className="md:mt-6 !mt-4 flex flex-wrap md:gap-2 gap-1">
        {REPORT_TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => handleTabChange(key)}
            className={`md:px-6 !px-4 md:py-2.5 !py-2 max-[767px]:text-xs !rounded-full border transition-all duration-200 ${
              activeTab === key
                ? "!bg-danger text-white !border-danger"
                : "text-gray-700 border-gray-500 hover:!border-danger hover:!bg-danger hover:!text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="md:mt-6 !mt-4 rounded-xl border border-gray-200 bg-white md:!p-4 !p-3 shadow-sm max-[767px]:text-sm">
        <div className="flex flex-col md:gap-4 !gap-2 lg:flex-row lg:flex-wrap lg:items-end">
          {isDriverSalaryReport ? (
            <>
              <div className="min-w-0 flex-1 sm:max-w-xs">
                <label
                  className="mb-1 block text-sm font-UbuntuMedium text-gray-700"
                  htmlFor="driver-salary-search"
                >
                  Search
                </label>
                <input
                  id="driver-salary-search"
                  type="search"
                  placeholder="Search by name or ID..."
                  className="searchIcon h-[44px] w-full rounded-lg border border-[#0B3E64]/40 bg-white px-3 text-sm outline-none focus:border-[#0B3E64]"
                  value={salarySearch}
                  maxLength={SEARCH_MAX_LENGTH}
                  onChange={(e) => setSalarySearch(clampSearchInput(e.target.value))}
                  onPaste={(e) => {
                    e.preventDefault();
                    setSalarySearch(
                      normalizePastedSearchInput(e.clipboardData.getData("text"))
                    );
                  }}
                />
              </div>

              <div className="min-w-0 flex-1 sm:max-w-xs">
                <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">
                  MIPL Location
                </label>
                <LocationSelect
                  value={miplLocationId}
                  onChange={(e) => setMiplLocationId(e.target.value)}
                  locations={miplLocations}
                  loading={locationsLoading}
                />
              </div>

              <MonthYearPicker
                id="driver-salary-month-year"
                month={salaryMonth}
                year={salaryYear}
                onChange={({ month, year }) => {
                  setSalaryMonth(month);
                  setSalaryYear(year);
                }}
              />

              <div className="min-w-0 flex-1 sm:max-w-xs">
                <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">
                  Driver
                </label>
                <LocationSelect
                  value={salaryDriverId}
                  onChange={(e) => setSalaryDriverId(e.target.value)}
                  locations={driverOptions.map((d) => ({
                    id: d.id,
                    name: d.refId ? `${d.name} (${d.refId})` : d.name,
                  }))}
                  loading={driversLoading}
                  ariaLabel="Driver"
                  placeholder="All drivers"
                  loadingPlaceholder="Loading drivers…"
                />
              </div>
            </>
          ) : (
            <>
            <div className="[&_.relative_button]:w-full [&_.relative_button_img]:h-4">
              <DateRangeFilter
                variant="field"
                label="Date range"
                from={dateFrom}
                to={dateTo}
                onChange={({ from, to }) => {
                  setDateFrom(from);
                  setDateTo(to);
                }}
                onApply={handleApplyDates}
                applyDisabled={!dateFrom || !dateTo}
                onClear={handleClearDates}
              />
              </div>

              <div className="min-w-0 flex-1 sm:max-w-xs">
                <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">
                  MIPL Location
                </label>
                <LocationSelect
                  value={miplLocationId}
                  onChange={(e) => setMiplLocationId(e.target.value)}
                  locations={miplLocations}
                  loading={locationsLoading}
                />
              </div>

              {isTripReport ? (
                <>
                  <div className="min-w-0 flex-1 sm:max-w-xs">
                    <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">
                      Company Name
                    </label>
                    <LocationSelect
                      value={companyId}
                      onChange={(e) => {
                        setCompanyId(e.target.value);
                        setCompanyLocationId("");
                      }}
                      locations={companyList}
                      loading={companiesLoading}
                      ariaLabel="Company Name"
                      placeholder="All companies"
                      loadingPlaceholder="Loading companies…"
                    />
                  </div>

                  <div className="min-w-0 flex-1 sm:max-w-xs">
                    <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">
                      Company Location
                    </label>
                    <LocationSelect
                      value={companyLocationId}
                      onChange={(e) => setCompanyLocationId(e.target.value)}
                      locations={companyLocations}
                      loading={companyLocationsLoading}
                      disabled={!companyId}
                      ariaLabel="Company Location"
                      placeholder="All company locations"
                      loadingPlaceholder="Loading company locations…"
                      disabledPlaceholder="Select company first"
                    />
                  </div>
                </>
              ) : null}
            </>
          )}

          {hasAnyFilter ? (
            <button
              type="button"
              onClick={handleClearFilters}
              className="h-[44px] shrink-0 self-end rounded-lg border border-gray-300 bg-white px-5 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          ) : null}

          <PermissionGate module={MODULES.REPORTS} action="export">
            <button
              type="button"
              onClick={handleExport}
              disabled={exportLoading || logLoading}
              className="btn btn-primary ml-auto"
            >
              {exportLoading ? "Exporting…" : "Export"}
            </button>
          </PermissionGate>
        </div>

        <p className="mb-0 mt-3 text-sm text-gray-600">
          <span className="font-UbuntuMedium text-gray-800">{activeTabConfig.label} export log</span>
          {" · "}
          {isDriverSalaryReport ? (
            <>
              {selectedSalaryMonthLabel}
              {" · "}
              {selectedLocationName}
              {" · "}
              {selectedDriverName}
              {salarySearch.trim() ? (
                <>
                  {" · "}
                  Search: {salarySearch.trim()}
                </>
              ) : null}
            </>
          ) : (
            <>
              {rangeText}
              {" · "}
              {selectedLocationName}
              {isTripReport ? (
                <>
                  {" · "}
                  {selectedCompanyName}
                  {" · "}
                  {selectedCompanyLocationName}
                </>
              ) : null}
            </>
          )}
          {!hasAnyFilter ? (
            <span className="text-gray-500"> · Export without filters uses the report endpoint only</span>
          ) : null}
        </p>
      </div>

      <div className="common-table-wrapper m-0 mt-4 shadow-sm">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                {logColumns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="max-[767px]:[&_td]:!py-2">
              {logLoading && (
                <tr>
                  <td colSpan={colSpan} className="py-4 text-center text-gray-500">
                    Loading log…
                  </td>
                </tr>
              )}
              {!logLoading && logRows.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="py-8 text-center text-gray-500">
                    No export log entries yet. Click Export to generate a report.
                  </td>
                </tr>
              )}
              {!logLoading &&
                logRows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td className="max-w-[220px] truncate" title={row.fileName}>
                      {row.fileName}
                    </td>
                    {isDriverSalaryReport ? (
                      <>
                        <td>{row.month}</td>
                        <td>{row.location}</td>
                        <td>{row.driver}</td>
                      </>
                    ) : (
                      <>
                        <td>{row.dateRange}</td>
                        <td>{row.location}</td>
                        {isTripReport ? (
                          <>
                            <td>{row.companyName}</td>
                            <td>{row.companyLocation}</td>
                          </>
                        ) : null}
                      </>
                    )}
                    <td className="whitespace-nowrap">{row.createdAt}</td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                    <td>
                      {row.status === "Complete" && row.downloadUrl ? (
                        <button
                          type="button"
                          onClick={() => handleDownload(row.downloadUrl)}
                          className="inline-flex md:h-9 md:w-9 h-7 w-7 items-center justify-center rounded-full border-0 !bg-primary"
                          aria-label={`Download ${row.fileName}`}
                        >
                          <img src={DownloadIcon} alt="" className="md:h-3.5 md:w-3.5 h-2.5 w-2.5" />
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <Toastify />
    </>
  );
};

export default Reports;
